<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useId, watch } from 'vue'
import { useRouter } from 'vue-router'

import UigIcon from '@/components/UigIcon.vue'
import { capture } from '@/services/analytics'
import { api } from '@/services/api'
import { mediaFileName, shareMedia } from '@/services/mediaFile'
import { isActivePhase, useGenerationsStore, type TrackedGeneration } from '@/stores/generations'

const props = defineProps<{
  generation: TrackedGeneration
  composer?: boolean
}>()

const emit = defineEmits<{
  close: []
  regenerate: []
  reset: []
}>()

const router = useRouter()
const generations = useGenerationsStore()
const videoSourceTypes = ['image/jpeg', 'image/png', 'image/webp']
const videoSourceMaxBytes = 20 * 1024 * 1024
const titleId = `result-title-${useId()}`
const resultUrl = ref('')
const resultBlob = ref<Blob | null>(null)
const loadingResult = ref(false)
const loadError = ref('')
const actionError = ref('')
const shareNotice = ref('')
const sharing = ref(false)
let loadVersion = 0

const inProgress = computed(() => isActivePhase(props.generation.phase))
const loading = computed(() => inProgress.value || loadingResult.value)
const ready = computed(() => props.generation.phase === 'succeeded' && Boolean(resultUrl.value))
const isPhoto = computed(() => props.generation.family === 'image')
const failed = computed(() => !loading.value && !ready.value)
const resultDownloadName = computed(() => resultBlob.value
  ? mediaFileName(resultBlob.value, props.generation.family === 'video') : '')
const errorMessage = computed(() => loadError.value || props.generation.error)
const title = computed(() => {
  if (ready.value) return props.generation.family === 'video' ? 'Your Video' : 'AI Image'
  if (failed.value) return loadError.value ? 'Result needs a reload' : 'Could not create'
  return 'Creating...'
})
const loadingTitle = computed(() => {
  if (loadingResult.value) return 'Loading your result'
  if (props.generation.phase === 'uploading') return 'Preparing your files'
  if (props.generation.phase === 'queued') return 'Waiting for generation'
  return 'Making your creation'
})
const loadingText = computed(() => {
  if (loadingResult.value) return 'Your creation is ready. Opening it now.'
  if (props.generation.phase === 'uploading') return 'Your media is uploading securely.'
  return 'This can take a little while. Your result will appear here.'
})

function releaseResult() {
  if (resultUrl.value) URL.revokeObjectURL(resultUrl.value)
  resultUrl.value = ''
  resultBlob.value = null
}

async function loadResult() {
  const path = props.generation.resultPath
  if (!path) return
  const version = ++loadVersion
  loadingResult.value = true
  loadError.value = ''
  try {
    const blob = await api.mediaBlob(path)
    if (version !== loadVersion) return
    releaseResult()
    resultBlob.value = blob
    resultUrl.value = URL.createObjectURL(blob)
  } catch {
    if (version === loadVersion) loadError.value = 'Your result is ready, but it could not be loaded. Please try again.'
  } finally {
    if (version === loadVersion) loadingResult.value = false
  }
}

function retry() {
  if (loadError.value) {
    void loadResult()
    return
  }
  if (props.generation.stalled) {
    generations.check(props.generation.id)
    return
  }
  emit('regenerate')
}

async function shareResult() {
  const blob = resultBlob.value
  if (!blob || !resultUrl.value || sharing.value) return
  const { jobId, family } = props.generation
  sharing.value = true
  actionError.value = ''
  shareNotice.value = ''
  try {
    const action = await shareMedia(blob, resultDownloadName.value)
    if (action === 'downloaded') {
      capture('generation_downloaded', {
        job_id: jobId, family, source: 'result', method: 'share_fallback',
      })
      if (resultBlob.value === blob) shareNotice.value = 'Sharing is not available in this browser, so the file was downloaded.'
    }
  } catch {
    if (resultBlob.value === blob) actionError.value = 'Failed to open share menu. Please try again.'
  } finally {
    if (resultBlob.value === blob) sharing.value = false
  }
}

function makeVideo() {
  const blob = resultBlob.value
  if (!blob) return
  if (!videoSourceTypes.includes(blob.type) || blob.size > videoSourceMaxBytes) {
    actionError.value = 'Could not prepare this photo for video. Please try again.'
    return
  }
  const source = generations.offerVideoSource(new File([blob], resultDownloadName.value, { type: blob.type }))
  emit('close')
  void router.push({ path: '/create', query: { family: 'video', source } })
}

watch(
  () => [props.generation.id, props.generation.phase, props.generation.resultPath] as const,
  ([, phase, path]) => {
    loadVersion += 1
    loadingResult.value = false
    loadError.value = ''
    actionError.value = ''
    shareNotice.value = ''
    sharing.value = false
    releaseResult()
    if (phase === 'succeeded' && path) void loadResult()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  loadVersion += 1
  releaseResult()
})
</script>

<template>
  <section
    class="result-overlay"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
  >
    <div class="result-view">
      <header
        class="result-header"
        :class="{ 'result-header--video': generation.family === 'video' }"
      >
        <button
          class="result-back"
          type="button"
          aria-label="Close result"
          @click="emit('close')"
        >
          <UigIcon
            name="xmark"
            :size="22"
          />
        </button>
        <h2 :id="titleId">
          {{ title }}
        </h2>
        <button
          v-if="ready && isPhoto"
          class="result-back result-download"
          type="button"
          :aria-label="sharing ? 'Opening download' : 'Download'"
          :aria-busy="sharing"
          :disabled="sharing"
          @click="shareResult"
        >
          <UigIcon
            name="arrow_down_to_line_alt"
            :size="22"
          />
        </button>
        <span
          v-else
          class="result-header__spacer"
        />
      </header>

      <div
        v-if="loading"
        class="result-stage result-stage--loading"
        role="status"
        aria-live="polite"
      >
        <div
          class="result-loader"
          aria-hidden="true"
        >
          <UigIcon
            name="sparkles"
            :size="38"
          />
        </div>
        <strong>{{ loadingTitle }}</strong>
        <p>{{ loadingText }}</p>
      </div>

      <div
        v-else-if="ready"
        class="result-stage result-stage--complete"
      >
        <video
          v-if="generation.family === 'video'"
          :src="resultUrl"
          controls
          autoplay
          muted
          loop
          playsinline
        />
        <img
          v-else
          :src="resultUrl"
          alt="Your generated creation"
        >
      </div>

      <div
        v-else
        class="result-stage result-stage--failed"
        role="alert"
      >
        <UigIcon
          name="xmark"
          :size="34"
        />
        <strong>{{ loadError ? 'Your result is ready' : 'Generation failed' }}</strong>
        <p>{{ errorMessage }}</p>
      </div>

      <footer class="result-actions">
        <p
          v-if="actionError || shareNotice"
          class="inline-error"
          :role="actionError ? 'alert' : 'status'"
        >
          {{ actionError || shareNotice }}
        </p>
        <template v-if="ready">
          <button
            v-if="isPhoto"
            class="result-action result-action--primary result-make-video"
            type="button"
            @click="makeVideo"
          >
            <UigIcon
              name="play_rectangle_fill"
              :size="20"
            />
            Make a video
          </button>
          <button
            v-else
            class="result-action result-action--primary result-download"
            type="button"
            :disabled="sharing"
            @click="shareResult"
          >
            <UigIcon
              name="arrow_down_to_line_alt"
              :size="20"
            />
            {{ sharing ? 'Opening...' : 'Download' }}
          </button>
          <div
            v-if="composer"
            class="result-actions__secondary"
          >
            <button
              class="result-action result-action--secondary"
              type="button"
              :disabled="!generations.canStart"
              @click="emit('regenerate')"
            >
              <UigIcon
                name="arrow_2_circlepath"
                :size="18"
              />
              Regenerate
              <UigIcon
                name="bolt_fill"
                :size="13"
              />
              {{ generation.cost }}
            </button>
            <button
              class="result-action result-action--secondary"
              type="button"
              @click="emit('reset')"
            >
              <UigIcon
                name="sparkles"
                :size="18"
              />
              Create Again
            </button>
          </div>
        </template>
        <template v-else-if="failed">
          <button
            v-if="composer || loadError || generation.stalled"
            class="result-action result-action--primary"
            type="button"
            :disabled="!loadError && !generations.canStart"
            @click="retry"
          >
            {{ loadError ? 'Reload result' : generation.stalled ? 'Check again' : 'Try again' }}
          </button>
          <button
            class="result-action result-action--secondary"
            type="button"
            @click="composer ? emit('reset') : emit('close')"
          >
            {{ composer ? 'Back to editor' : 'Close' }}
          </button>
        </template>
        <template v-else-if="inProgress">
          <button
            class="result-action result-action--secondary"
            type="button"
            @click="emit('close')"
          >
            Continue in background
          </button>
          <p>
            Your creation will keep generating. We will let you know when it is ready.
          </p>
        </template>
      </footer>
    </div>
  </section>
</template>
